import { v1 as uuid } from 'uuid';

import {
  ActionVisibilityValueType,
  OutcomeActionType,
  OutcomeType,
} from 'utils/adl-services';
import {
  isMatrixNode,
  isConversationalType,
  isGroup,
  isResearchMethod,
} from 'utils/nodeTypes';
import {
  QUESTION_MATRIX_BIPOLAR,
  QUESTION_MATRIX_CONSTANT_SUM,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_MATRIX_NUMERIC_OPEN_ENDED,
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MATRIX_TYPE_LIKERT,
  QUESTION_MULTIPLE,
  QUESTION_RANKING,
  QUESTION_SINGLE,
} from 'data/nodeTypes';
import { getAnswerObjective } from 'containers/QuestionnairePage/GenericQuestion/ConversationalQuestion/utils';

import messages from 'containers/QuestionnairePage/messages';
import { LOGIC_MODES } from 'containers/QuestionnairePage/constants';
import { getPromptFromHtml } from 'components/FormattedInput/helpers';
import { getLabel } from 'components/LogicRule/utils';
import {
  canShowHideAnswerOnlyInRows,
  isNodeSupportConditionalDisplayLogicOnAnswers,
} from 'components/VisibilityActionButton/utils';
import {
  actionTypeKeyGroup,
  actionTypeKeyResearchMethod,
} from '../LogicOutcomeAction/constants';
import {
  outcomeActionTypes,
  supportsTargeIdOnDefaultOutcome,
  supportsValueTypeOnAction,
} from './constants';

export function getDefaultTargetIdForAction(logicMode, targetId) {
  return supportsTargeIdOnDefaultOutcome.includes(logicMode)
    ? targetId
    : undefined;
}

export function getDefaultValueTypeByTargetType(targetNodeType) {
  if (!isNodeSupportConditionalDisplayLogicOnAnswers(targetNodeType)) {
    return ActionVisibilityValueType.Node;
  }

  if (isMatrixNode({ style: targetNodeType })) {
    return ActionVisibilityValueType.Row;
  }

  return ActionVisibilityValueType.Answer;
}

export function getDefaultValueTypeForAction(logicMode, targetNodeType) {
  return supportsValueTypeOnAction.includes(logicMode)
    ? getDefaultValueTypeByTargetType(targetNodeType)
    : undefined;
}

export function getDefaultOutcomeAction(logicMode, targetId, targetNodeType) {
  const type = outcomeActionTypes[logicMode];

  if (!type) {
    return undefined;
  }

  const valueTypeForAction = getDefaultValueTypeForAction(
    logicMode,
    targetNodeType,
  );

  return {
    type,
    id: uuid(),
    targetId: getDefaultTargetIdForAction(logicMode, targetId),
    valueType: valueTypeForAction,
    value:
      valueTypeForAction === ActionVisibilityValueType.Node ? '' : undefined,
  };
}

export function getOutcomeAction({ logicMode, targetId, value, valueType }) {
  const type = outcomeActionTypes[logicMode];

  if (!type) {
    return undefined;
  }

  return {
    type,
    id: uuid(),
    targetId: getDefaultTargetIdForAction(logicMode, targetId),
    valueType,
    value,
  };
}

export function getOutcomeType(logicMode, actionVisibilityValueType) {
  switch (logicMode) {
    case LOGIC_MODES.HIDDEN_VARIABLES:
      return OutcomeType.Variable;
    case LOGIC_MODES.DISPLAY_LOGIC: {
      if (actionVisibilityValueType === ActionVisibilityValueType.Node) {
        return OutcomeType.Node;
      }

      return OutcomeType.Answer;
    }
    case LOGIC_MODES.ADVANCED_QUOTA_LOGIC:
      return OutcomeType.Failure;
    default:
      return OutcomeType.Success;
  }
}

function getDefaultOutcomeActionValue(valueType, valueRefId) {
  switch (valueType) {
    case ActionVisibilityValueType.Node:
      return '';
    case ActionVisibilityValueType.Answer:
      return [valueRefId];
    default:
      return valueRefId;
  }
}

export function getDefaultStatementWithOutcomeAction({
  valueRefId,
  nodeRefId,
  valueType,
  scrollTo = false,
  logicMode = LOGIC_MODES.DISPLAY_LOGIC,
}) {
  return {
    nodeRefId,
    outcomeType: getOutcomeType(logicMode, valueType),
    logicMode,
    outcomeAction: getOutcomeAction({
      logicMode,
      targetId: nodeRefId,
      valueType,
      value: getDefaultOutcomeActionValue(valueType, valueRefId),
    }),
    scrollTo,
  };
}

function mapValueItem(item, valueType) {
  return {
    label: getLabel(
      getPromptFromHtml(item.beforeLabel || item.body),
      valueType,
    ),
    refId: item.answerRefId || item.refId, // for matrixAnswers we use the answerRefId in adl rule
    isCarriedForward: item.isCarriedForward,
    valueType,
  };
}

export function getDefaultValuesForAnswersLogicMode(node) {
  const { answers = [], scorepoints = [], matrix = {} } = node;
  if (isGroup(node)) {
    return [];
  }

  if (matrix.matrixAnswers) {
    const answerItems = matrix.matrixAnswers.map((item) =>
      mapValueItem(item, ActionVisibilityValueType.Row),
    );

    if (canShowHideAnswerOnlyInRows(node)) {
      return answerItems;
    }

    return [
      ...answerItems,
      ...matrix.columns.map((item) =>
        mapValueItem(item, ActionVisibilityValueType.Column),
      ),
    ];
  }

  const valueType = QUESTION_MATRIX_TYPE_LIKERT.includes(node.type)
    ? ActionVisibilityValueType.Row
    : ActionVisibilityValueType.Answer;

  return [
    ...answers.map((answer) => mapValueItem(answer, valueType)),
    ...scorepoints.map((scorepoint) =>
      mapValueItem(scorepoint, ActionVisibilityValueType.Column),
    ),
  ];
}

export function mergeRowsWithColumns(node) {
  const { answers = [], scorepoints = [], matrix = {} } = node;

  if (matrix.matrixAnswers) {
    if (canShowHideAnswerOnlyInRows(node)) {
      return matrix.matrixAnswers;
    }

    return [...matrix.matrixAnswers, ...matrix.columns];
  }

  return [...answers, ...scorepoints];
}

export function getTargetValues(logicMode, values, node) {
  switch (logicMode) {
    case LOGIC_MODES.HIDDEN_VARIABLES:
      return values;
    case LOGIC_MODES.DISPLAY_LOGIC: {
      let label = node.prompt;

      if (isGroup(node.type)) {
        label = node.label;
      } else if (isConversationalType(node.type)) {
        const objective = getAnswerObjective(node);

        label =
          objective?.body || messages.conversationalQuestion.defaultMessage;
      }

      return [
        {
          label,
          refId: node.refId,
          values: getDefaultValuesForAnswersLogicMode(node),
        },
      ];
    }
    default:
      return [];
  }
}

export function getAvailableActionTypes(logicMode, node) {
  if (logicMode === LOGIC_MODES.HIDDEN_VARIABLES) {
    return [OutcomeActionType.Set, OutcomeActionType.Unset];
  }

  if (logicMode === LOGIC_MODES.DISPLAY_LOGIC) {
    switch (node.type) {
      case QUESTION_SINGLE:
      case QUESTION_MULTIPLE:
      case QUESTION_RANKING:
        return [
          ActionVisibilityValueType.Node,
          ActionVisibilityValueType.Answer,
        ];
      case QUESTION_MATRIX_CONSTANT_SUM:
      case QUESTION_MATRIX_BIPOLAR:
        return [ActionVisibilityValueType.Node, ActionVisibilityValueType.Row];
      case QUESTION_MATRIX_SINGLE:
      case QUESTION_MATRIX_MULTIPLE:
      case QUESTION_MATRIX_OPEN_ENDED:
      case QUESTION_MATRIX_NUMERIC_OPEN_ENDED:
        return [
          ActionVisibilityValueType.Node,
          ActionVisibilityValueType.Row,
          ActionVisibilityValueType.Column,
        ];
      default:
        return [ActionVisibilityValueType.Node];
    }
  }

  return [];
}

export function getEnrichedAvailableActionTypes(logicMode, node) {
  const availableActionTypes = getAvailableActionTypes(logicMode, node).map(
    (actionType) => ({ key: actionType, value: actionType }),
  );

  if (logicMode === LOGIC_MODES.DISPLAY_LOGIC && isGroup(node)) {
    const actionTypeKey = isResearchMethod(node)
      ? actionTypeKeyResearchMethod
      : actionTypeKeyGroup;

    return availableActionTypes.map((actionType) => {
      if (actionType.value === ActionVisibilityValueType.Node) {
        return {
          ...actionType,
          key: actionTypeKey,
        };
      }

      return actionType;
    });
  }

  return availableActionTypes;
}
